<template>
    <section id="floorplan-fullscreen">
        <div class="floorplan-controls">
            <div class="controls">
                <div class="dates-filter-container">
                    <ci-button
                        @click="isDatesFilterOpen = !isDatesFilterOpen"
                        :centerIcon="{ name: 'ci-calendar', color: selectedDatesArray.length > 0 ? 'var(--secondary)' : 'white' }"
                        type="primary-1"
                        :class="{ 'filter-active': selectedDatesArray.length > 0 }"
                    />
                    <div v-show="isDatesFilterOpen">
                        <ci-filter-date-select class="dates-filter" :dates="project.capture_dates" v-model="selectedDatesArray" />
                    </div>
                </div>
                <div @click="resizeViewpoints" class="fp-control">
                    <icon v-if="getViewpointsSize() == 1" name="ci-resize-circle" color="white" scale=".75" />
                    <icon v-if="getViewpointsSize() == 2" name="ci-resize-circle-plus" color="white" scale="1.25" />
                    <icon v-if="getViewpointsSize() == 3" name="ci-resize-circle-plus-plus" color="white" scale="1.75" />
                </div>
                <div @click="zoomIn" class="fp-control">
                    <icon name="search-plus" scale=".75" />
                </div>
                <div @click="closeFullscreen" id="fp-control-expand" class="fp-control fp-control-expand">
                    <icon name="expand" scale=".75" />
                </div>
                <div @click="zoomOut" class="fp-control">
                    <icon name="search-minus" scale=".75" />
                </div>
                <div @click="resetZoomer" class="fp-control">
                    <icon name="arrows-alt-h" scale=".75" color="white" />
                </div>
            </div>
        </div>
        <div class="floorplan-img">
            <FloorplanCanvas
                ref="zoomer"
                v-if="currentFloorplan.url"
                :floorplan="currentFloorplan.url"
                :viewpoints="currentFloorplan.viewpoints"
                :viewpointSize="getViewpointsSize()"
                :grayscaleOnViewpointSelected="false"
                :selectedViewpoint="currentViewpoint.id"
                :enableViewpointNameTooltip="true"
                :hoveredViewpoint="hoveredViewpoint.id"
                :datesToFilter="selectedDatesArray"
                @viewpointClick="viewpointClicked"
                @viewpointHover="(vp) => (hoveredViewpoint = vp)"
            />
        </div>
        <div></div>
        <div class="floorplan-selector">
            <div class="selector">
                <div @click="prevFloorplan" class="prev-button d-flex justify-content-center align-items-center" v-b-tooltip.hover :title="prevFloorplanName">
                    <icon name="chevron-left" />
                </div>
                <div class="fp-name d-flex align-items-center justify-content-center">
                    <p class="text-12 text-weight-bold text-overflow" :title="currentFloorplan.name">
                        {{ currentFloorplan.name }}
                    </p>
                </div>
                <div @click="nextFloorplan" class="next-button d-flex justify-content-center align-items-center" v-b-tooltip.hover :title="nextFloorplanName">
                    <icon name="chevron-right" />
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import FloorplanCanvas from '@/components/FloorplanCanvas'
import 'vue-awesome/icons/chevron-left'
import 'vue-awesome/icons/chevron-right'

export default {
    components: {
        FloorplanCanvas,
    },
    props: {
        /**
         * current sector of the showing viewpoint
         */
        currentSector: {
            type: null,
        },
        /**
         * current floorplan of the showing viewpoint
         */
        currentFloorplan: {
            type: null,
        },
        /**
         * current viewpoint that is showing
         */
        currentViewpoint: {
            type: null,
        },
        /**
         * the entire project
         */
        project: {
            type: null,
        },
        /**
         * determines if project is a map or not
         */
        isMap: {
            type: Boolean,
        },
    },
    computed: {
        ...mapState(['sidebar']),
        ...mapState(['dateFilterViewer']),
        ...mapGetters('viewpointsSize', ['getById']),
        ...mapGetters('dateFilterViewer', ['getDatesByProjectId']),
        nextFloorplanName() {
            if (this.isMap) {
                let auxIndex = this.currentSector.floorplans.indexOf(this.currentFloorplan)
                if (auxIndex >= this.currentSector.floorplans.length - 1) {
                    auxIndex = 0
                } else {
                    auxIndex++
                }
                return this.currentSector.floorplans[auxIndex].name
            } else {
                let auxIndex = this.project.floorplans.indexOf(this.currentFloorplan)
                if (auxIndex >= this.project.floorplans.length - 1) {
                    auxIndex = 0
                } else {
                    auxIndex++
                }
                return this.project.floorplans[auxIndex].name
            }
        },
        prevFloorplanName() {
            if (this.isMap && this.currentSector.floorplans) {
                let auxIndex = this.currentSector.floorplans.indexOf(this.currentFloorplan)
                if (auxIndex <= 0) {
                    auxIndex = this.currentSector.floorplans.length - 1
                } else {
                    auxIndex--
                }
                return this.currentSector.floorplans[auxIndex].name
            } else {
                let auxIndex = this.project.floorplans.indexOf(this.currentFloorplan)
                if (auxIndex <= 0) {
                    auxIndex = this.project.floorplans.length - 1
                } else {
                    auxIndex--
                }

                return this.project.floorplans[auxIndex].name
            }
        },
    },
    watch: {
        dateFilterViewer: {
            handler() {
                this.selectedDatesArray = this.getDatesByProjectId(this.project.id)
            },
            deep: true,
        },
        selectedDatesArray() {
            this.setDates({ projectId: this.project.id, dates: this.selectedDatesArray })
        },
    },
    data() {
        return {
            isViewpointResizerOpen: false,
            isViewpointResizerOpen: false,
            isFilterOn: false,
            highestPriorities: [],
            floorplanIndex: -1,
            hoveredViewpoint: -1,
            isDatesFilterOpen: false,
            selectedDatesArray: [],
        }
    },
    methods: {
        ...mapActions('viewpointsSize', ['addProject', 'addFloorplan']),
        ...mapActions('dateFilterViewer', ['setDates']),
        viewpointClicked(data) {
            let vpAux = {}
            this.currentFloorplan.viewpoints.forEach((vp) => {
                if (vp.id == data.id) {
                    vpAux = vp
                }
            })

            this.$emit('viewpointClick', vpAux)
            this.closeFullscreen()
        },
        closeFullscreen() {
            this.$emit('closeFloorplanFullscreen')
        },
        resizeViewpoints() {
            if (this.getViewpointsSize() < 3) {
                this.updateViewpointsSize(this.getViewpointsSize() + 1)
            } else {
                this.updateViewpointsSize(1)
            }
        },
        getViewpointsSize() {
            let projectSizeInfo = this.getById(this.project.id)

            if (projectSizeInfo) {
                if (this.isMap) {
                    if (this.currentFloorplan.id == this.project.map.id) {
                        return projectSizeInfo.mapSize
                    } else {
                        let fp = projectSizeInfo.floorplans.find((fp) => fp.id == this.currentFloorplan.id)
                        if (fp) {
                            return fp.size
                        } else {
                            this.updateViewpointsSize()
                        }
                    }
                } else {
                    let fp = projectSizeInfo.floorplans.find((fp) => fp.id == this.currentFloorplan.id)
                    if (fp) {
                        return fp.size
                    } else {
                        this.updateViewpointsSize()
                    }
                }
            } else {
                this.updateViewpointsSize()
            }

            return 2
        },
        updateViewpointsSize(vpSize = 2) {
            if (this.isMap) {
                if (this.currentFloorplan.id == this.project.map.id) {
                    this.addProject({ id: this.project.id, mapSize: vpSize })
                } else {
                    this.addFloorplan({ project_id: this.project.id, id: this.currentFloorplan.id, size: vpSize })
                }
            } else {
                this.addFloorplan({ project_id: this.project.id, id: this.currentFloorplan.id, size: vpSize })
            }
        },
        zoomIn() {
            let zoomer = this.$refs.zoomer || this.$refs['zoomer-fullscreen']
            zoomer.zoomIn()
        },
        zoomOut() {
            let zoomer = this.$refs.zoomer || this.$refs['zoomer-fullscreen']
            zoomer.zoomOut()
        },
        resetZoomer() {
            let zoomer = this.$refs.zoomer || this.$refs['zoomer-fullscreen']
            zoomer.resetScale()
        },
        openViewpointResizer() {
            this.isViewpointResizerOpen = !this.isViewpointResizerOpen
        },
        formatTooltip(val) {
            if (val == 1) return this.$t('keyword59')
            else if (val == 2) return this.$t('keyword60')
            else return this.$t('keyword61')
        },
        nextFloorplan() {
            if (this.isMap) {
                this.currentSector.floorplans.forEach((fp, index) => {
                    if (fp == this.currentFloorplan) {
                        this.floorplanIndex = index
                    }
                })
                if (this.floorplanIndex >= this.currentSector.floorplans.length - 1) {
                    this.floorplanIndex = 0
                } else {
                    this.floorplanIndex++
                }
                this.$emit('floorplanChange', this.currentSector.floorplans[this.floorplanIndex])
            } else {
                this.project.floorplans.forEach((fp, index) => {
                    if (fp == this.currentFloorplan) {
                        this.floorplanIndex = index
                    }
                })
                if (this.floorplanIndex >= this.project.floorplans.length - 1) {
                    this.floorplanIndex = 0
                } else {
                    this.floorplanIndex++
                }
                this.$emit('floorplanChange', this.project.floorplans[this.floorplanIndex])
            }
        },
        prevFloorplan() {
            if (this.isMap) {
                this.currentSector.floorplans.forEach((fp, index) => {
                    if (fp == this.currentFloorplan) {
                        this.floorplanIndex = index
                    }
                })
                if (this.floorplanIndex <= 0) {
                    this.floorplanIndex = this.currentSector.floorplans.length - 1
                } else {
                    this.floorplanIndex--
                }
                this.$emit('floorplanChange', this.currentSector.floorplans[this.floorplanIndex])
            } else {
                this.project.floorplans.forEach((fp, index) => {
                    if (fp == this.currentFloorplan) {
                        this.floorplanIndex = index
                    }
                })
                if (this.floorplanIndex <= 0) {
                    this.floorplanIndex = this.project.floorplans.length - 1
                } else {
                    this.floorplanIndex--
                }
                this.$emit('floorplanChange', this.project.floorplans[this.floorplanIndex])
            }
        },
    },
    mounted() {
        this.selectedDatesArray = this.getDatesByProjectId(this.project.id)
        document.body.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                this.closeFullscreen()
            }

            if (e.keyCode == 39) {
                this.nextFloorplan()
            }

            if (e.keyCode == 37) {
                this.prevFloorplan()
            }
        })
    },
}
</script>

<style lang="scss" scoped>
@import '@/scss/components/360Viewer/Sidebar/Floorplans/floorplan-fullscreen.scss';
</style>
